import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocalUserPreference } from '@percent/cause-dashboard/common/hooks/useLocalUserPreference/useLocalUserPreference'

export enum SignUpReason {
  REVERIFICATION = 'reverify',
  VERIFICATION_STATUS_CHECK = 'verification_status_check',
  VERIFICATION_STATUS_CHECK_FOLLOW_UP = 'verification_status_check_follow_up',
  VERIFICATION_SUPPORT = 'verification_support',
  AV_EMAIL_FOOTER = 'av_email_footer',
  AV_DOCUMENT_UPLOAD = 'av_doc_upload',
  RECEIVE_DONATIONS = 'receive_donations',
  INVITED = 'invited',
  GRANTS_ONBOARDING = 'grants_onboarding',
  ORG_WEBSITE_POPUP_GRANTS = 'org_website_popup_grants',
  WPG_ONBOARDING = 'wpg_onboarding',
  DONATIONS_RECEIVED = 'donations_received',
  DISBURSEMENT_PARTNER_ONBOARDING = 'disbursement_partner_onboarding'
}

type SignUpAttribution = {
  reason?: string | null
  partnerId?: string | null
  emailVariant?: string | null
}

// The status-check follow-up email uses a distinct attribution reason so it can be told apart
// in analytics, but should behave identically to the original status-check sign up everywhere else.
export const isStatusCheckReason = (reason?: string | null): boolean =>
  reason === SignUpReason.VERIFICATION_STATUS_CHECK || reason === SignUpReason.VERIFICATION_STATUS_CHECK_FOLLOW_UP

const CACHE_EXPIRATION_HOURS = 24 * 30

export const useSignUpAttribution = () => {
  // Could expand to object with Attribution, campaign, medium etc in future
  const [cacheAttribution, setMySignUpAttributionInCache] = useLocalUserPreference('signUpAttribution')
  const [signUpAttribution, setSignUpAttribution] = useState<SignUpAttribution | null>(
    JSON.parse(cacheAttribution || '{}')
  )
  const { search } = useLocation()

  const fetchAttribution = useCallback(async () => {
    if (signUpAttribution?.partnerId || signUpAttribution?.reason) {
      return
    }
    const reason = new URLSearchParams(search).get('r')
    const partnerId = new URLSearchParams(search).get('p')
    const emailVariant = new URLSearchParams(search).get('v')

    if ((reason && Object.values(SignUpReason).includes(reason as any)) || partnerId || emailVariant) {
      const signUpAttribution = {
        reason,
        partnerId,
        emailVariant
      }
      setSignUpAttribution(signUpAttribution)
      setMySignUpAttributionInCache(JSON.stringify(signUpAttribution), {
        expiresInHours: CACHE_EXPIRATION_HOURS
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchAttribution()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setSignUpAttributionManually = (reason: SignUpReason) => {
    if (signUpAttribution?.partnerId || signUpAttribution?.reason) {
      return
    }

    setSignUpAttribution({ reason })
  }

  return {
    signUpAttribution,
    setSignUpAttributionManually
  }
}
